import React, { useState, useEffect } from "react";
import {
  Paper,
  Container,
  Typography,
  Grid,
  Button,
  Box,
  Breadcrumbs,
  Link,
  Divider,
  makeStyles,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import {
  Add as AddIcon,
  KeyboardBackspace as KeyboardBackspaceIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Update as UpdateIcon,
  NavigateNext as NavigateNextIcon,
  GetApp as ExcelIcon,
  PictureAsPdf as PdfIcon,
  ArrowDropUp as ArrowDropUpIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from "@material-ui/icons";

import ImportExportIcon from "@material-ui/icons/ImportExport";
import { useNavigate } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  root: {
    background: "linear-gradient(135deg, #1e243c 0%, #003a6b 100%)",
    color: "white",
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(1),
    borderRadius: theme.spacing(3),
    boxShadow: "0 8px 32px rgba(102, 126, 234, 0.2)",
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(255, 255, 255, 0.05)",
      backdropFilter: "blur(10px)",
      zIndex: 0,
    },
  },
  contentWrapper: {
    position: "relative",
    zIndex: 1,
  },
  backButton: {
    color: "white",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "50%",
    width: 40,
    height: 40,
    minWidth: 40,
    marginRight: theme.spacing(2),
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      transform: "translateY(-2px)",
      boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "1.5rem",
    },
  },
  titleSection: {
    display: "flex",
    alignItems: "center",
    flexGrow: 1,
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  mainTitle: {
    fontWeight: 700,
    fontSize: "1.3rem",
    marginBottom: theme.spacing(0.5),
    textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.5rem",
    },
  },
  breadcrumbsContainer: {
    "& .MuiBreadcrumbs-root": {
      color: "rgba(255, 255, 255, 0.9)",
    },
    "& .MuiBreadcrumbs-separator": {
      color: "rgba(255, 255, 255, 0.7)",
    },
  },
  breadcrumbLink: {
    color: "rgba(255, 255, 255, 0.8)",
    textDecoration: "none",
    fontSize: "0.9rem",
    fontWeight: 500,
    transition: "all 0.3s ease",
    cursor: "pointer",
    "&:hover": {
      color: "white",
      textDecoration: "none",
      textShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
    },
  },
  currentPage: {
    color: "white",
    fontSize: "0.9rem",
    fontWeight: 600,
    textShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
  },
  buttonContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    justifyContent: "flex-end",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      marginTop: theme.spacing(2),
    },
  },
  primaryButton: {
    background: "linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)",
    border: 0,
    borderRadius: theme.spacing(3),
    boxShadow: "0 6px 20px rgba(255, 105, 135, 0.3)",
    color: "white",
    height: 40,
    padding: theme.spacing(0, 2),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.80rem",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 25px rgba(255, 105, 135, 0.4)",
    },
  },

  HeaderButton: {
    background: "linear-gradient(45deg, #00509e 30%, #0074cc 90%)",
    border: 0,
    borderRadius: theme.spacing(3),
    boxShadow: "0 6px 20px rgba(0, 116, 204, 0.3)", // lighter blue glow
    color: "white",
    height: 40,
    padding: theme.spacing(0, 2),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.80rem",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 25px rgba(0, 116, 204, 0.5)", // brighter hover glow
    },
  },

  secondaryButton: {
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    borderRadius: theme.spacing(3),
    color: "white",
    height: 40,
    padding: theme.spacing(0, 2),
    fontWeight: 500,
    textTransform: "none",
    fontSize: "0.80rem",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.25)",
      transform: "translateY(-1px)",
      boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
    },
  },
  successButton: {
    background: "linear-gradient(45deg, #4CAF50 30%, #45a049 90%)",
    border: 0,
    borderRadius: theme.spacing(3),
    boxShadow: "0 6px 20px rgba(76, 175, 80, 0.3)",
    color: "white",
    height: 40,
    padding: theme.spacing(0, 2),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.80rem",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 25px rgba(76, 175, 80, 0.4)",
    },
  },
  warningButton: {
    background: "linear-gradient(45deg, #FF9800 30%, #F57C00 90%)",
    border: 0,
    borderRadius: theme.spacing(3),
    boxShadow: "0 6px 20px rgba(255, 152, 0, 0.3)",
    color: "white",
    height: 40,
    padding: theme.spacing(0, 2),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.80rem",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 25px rgba(255, 152, 0, 0.4)",
    },
  },
  deleteButton: {
    background: "linear-gradient(45deg, #f44336 30%, #d32f2f 90%)",
    border: 0,
    borderRadius: theme.spacing(3),
    boxShadow: "0 6px 20px rgba(244, 67, 54, 0.3)",
    color: "white",
    height: 40,
    padding: theme.spacing(0, 2),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.80rem",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 25px rgba(244, 67, 54, 0.4)",
    },
  },
  // Excel Button - Green gradient with professional styling
  excelButton: {
    background: "linear-gradient(45deg, #217346 30%, #1e6b42 90%)",
    border: 0,
    borderRadius: theme.spacing(3),
    boxShadow: "0 6px 20px rgba(33, 115, 70, 0.3)",
    color: "white",
    height: 42,
    padding: theme.spacing(0, 3),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.95rem",
    minWidth: "120px",
    transition: "all 0.3s ease",
    position: "relative",
    overflow: "hidden",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 25px rgba(33, 115, 70, 0.4)",
      background: "linear-gradient(45deg, #1e6b42 30%, #1a5d3a 90%)",
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background:
        "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
      transition: "left 0.5s",
    },
    "&:hover::before": {
      left: "100%",
    },
  },
  // PDF Button - Red gradient with professional styling
  pdfButton: {
    background: "linear-gradient(45deg, #DC2626 30%, #B91C1C 90%)",
    border: 0,
    borderRadius: theme.spacing(3),
    boxShadow: "0 6px 20px rgba(220, 38, 38, 0.3)",
    color: "white",
    height: 42,
    padding: theme.spacing(0, 3),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.95rem",
    minWidth: "120px",
    transition: "all 0.3s ease",
    position: "relative",
    overflow: "hidden",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 25px rgba(220, 38, 38, 0.4)",
      background: "linear-gradient(45deg, #B91C1C 30%, #991B1B 90%)",
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background:
        "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
      transition: "left 0.5s",
    },
    "&:hover::before": {
      left: "100%",
    },
  },
  buttonIcon: {
    marginRight: theme.spacing(1),
    fontSize: "1.2rem",
    filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
  },
  exportButtonGroup: {
    display: "flex",
    gap: theme.spacing(1.5),
    marginRight: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      marginRight: 0,
      marginBottom: theme.spacing(1),
      width: "100%",
      justifyContent: "center",
    },
  },
  mobileContainer: {
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
    display: "flex",
    justifyContent: "space-between",
    alignContent: "center",
  },
  // Enhanced button animations
  "@keyframes shimmer": {
    "0%": { transform: "translateX(-100%)" },
    "100%": { transform: "translateX(100%)" },
  },
  // Divider between export and action buttons
  buttonDivider: {
    width: "1px",
    height: "30px",
    backgroundColor: "rgba(255, 255, 255, 0.3)",
    margin: theme.spacing(0, 1),
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
}));

export default function TopView({ data }) {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  // Enhanced route path function with better error handling
  const routePath = (path) => {
    try {
      const userEmail = sessionStorage.getItem("email");

      if (userEmail === "route@watsoo.com" && path === "/dashboard") {
        navigate("/routeList");
        return;
      }

      if (path === "back") {
        if (data.subTitleThird === "Live Tracking") {
          navigate("/dashboard");
        } else {
          // Try to go back in history, fallback to specified path
          if (window.history.state && window.history.state.idx > 0) {
            navigate(-1);
          } else {
            navigate(data.backPathSecond || "/dashboard");
          }
        }
      } else {
        navigate(path);
      }
    } catch (error) {
      console.error("Navigation error:", error);
      // Fallback navigation
      navigate("/dashboard");
    }
  };

  // Create breadcrumb items
  const createBreadcrumbs = () => {
    const breadcrumbItems = [];

    if (data.subTitleFirst) {
      breadcrumbItems.push(
        <Link
          key="first"
          className={classes.breadcrumbLink}
          onClick={() => routePath(data.backPath)}
        >
          {data.subTitleFirst.replace(">", "").trim()}
        </Link>,
      );
    }

    if (data.subTitleSecond && data.subTitleSecond !== "Company >") {
      breadcrumbItems.push(
        <Link
          key="second"
          className={classes.breadcrumbLink}
          onClick={() => routePath(data.backPathSecond)}
        >
          {data.subTitleSecond.replace(">", "").trim()}
        </Link>,
      );
    }

    if (data.subTitleThird) {
      breadcrumbItems.push(
        <Typography key="current" className={classes.currentPage}>
          {data.subTitleThird}
        </Typography>,
      );
    }

    return breadcrumbItems;
  };

  // Render action buttons
  const renderActionButtons = () => {
    const buttons = [];
    const actionButtons = [];

    // Export Buttons (Excel & PDF) - Show if enabled

    const exportButtons = [];
    if (data?.customheader) {
      actionButtons.push(
        <div style={{ position: "relative" }}>
          <Button
            key="custom-header"
            className={classes.HeaderButton}
            startIcon={
              <Box display="flex" alignItems="center">
                <EditIcon className={classes.buttonIcon} />
              </Box>
            }
            onClick={() => data?.setCustomModalOpen(true)}
            variant={data?.isCustomHeaderApplied ? "contained" : "outlined"}
            style={{
              background: data?.isCustomHeaderApplied
                ? "linear-gradient(45deg, #FFD700 30%, #FFA000 90%)"
                : "linear-gradient(45deg, #00509e 30%, #0074cc 90%)",
              color: data?.isCustomHeaderApplied ? "#1e243c" : "white",
              fontWeight: data?.isCustomHeaderApplied ? 700 : 600,
              border: data?.isCustomHeaderApplied
                ? "1px solid #FFA000"
                : "none",
            }}
          >
            Custom Header
          </Button>
          {data?.isCustomHeaderApplied && (
            <Box
              position="absolute"
              top={-6}
              right={0}
              width={18}
              height={18}
              borderRadius="50%"
              border="2px solid white"
              boxShadow="0 2px 10px rgba(255, 107, 107, 0.6)"
              display="flex"
              alignItems="center"
              justifyContent="center"
              bgcolor="red"
              borderColor="red"
            >
              <Box
                style={{
                  color: "white",
                  fontSize: "15px",
                  fontWeight: "bold",
                  lineHeight: 1,
                }}
              >
                !
              </Box>
            </Box>
          )}
        </div>,
      );
    }

    if (data?.showExcelButton) {
      exportButtons.push(
        <Button
          key="excel"
          className={classes.excelButton}
          startIcon={<ExcelIcon className={classes.buttonIcon} />}
          onClick={() => data.getExportData("excel")}
        >
          Export Excel
        </Button>,
      );
    }
    if (data?.showExcelButton2) {
      exportButtons.push(
        <Button
          key="excel"
          className={classes.excelButton}
          startIcon={<ExcelIcon className={classes.buttonIcon} />}
          onClick={() => data.getExportDataEventWise("excel")}
        >
          Vehicle Wise Export Excel
        </Button>,
      );
    }
    if (data?.showExcelButton2) {
      exportButtons.push(
        <Button
          key="excel"
          className={classes.excelButton}
          startIcon={<ExcelIcon className={classes.buttonIcon} />}
          onClick={() => data.getExportData2("excel")}
        >
          Event Wise Export Excel
        </Button>,
      );
    }

    if (data?.showPdfButton) {
      exportButtons.push(
        <Button
          key="pdf"
          className={classes.pdfButton}
          startIcon={<PdfIcon className={classes.buttonIcon} />}
          onClick={() => data.getExportData("pdf")}
        >
          Export PDF
        </Button>,
      );
    }
    if (data?.importBuuton) {
      exportButtons.push(
        <Button
          key="pdf"
          className={classes.successButton}
          startIcon={<ImportExportIcon className={classes.buttonIcon} />}
          onClick={() => data.openExcelImportModal()}
        >
          {data?.importText}
        </Button>,
      );
    }

    // Add export buttons group
    if (exportButtons.length > 0) {
      buttons.push(
        <Box key="export-group" className={classes.exportButtonGroup}>
          {exportButtons}
        </Box>,
      );

      // Add divider if there are other buttons
      if (
        data?.addButtonPermission ||
        data?.addButton ||
        data?.editButton ||
        (data?.updatePermission?.length > 0 && data.updateButton)
      ) {
        buttons.push(<Box key="divider" className={classes.buttonDivider} />);
      }
    }

    // Add Button
    if (data?.addButtonPermission && data?.buttonIcon && data?.buttonText) {
      actionButtons.push(
        <Button
          key="add"
          className={classes.primaryButton}
          startIcon={<AddIcon className={classes.buttonIcon} />}
          onClick={() => routePath(data?.addButtonPath)}
        >
          {data?.buttonText}
        </Button>,
      );
    }

    if (data?.isDialog && data?.handleOpenDialog && data?.addButtonPermission) {
      actionButtons.push(
        <Button
          key="add"
          className={classes.primaryButton}
          startIcon={<AddIcon className={classes.buttonIcon} />}
          onClick={() => data.handleOpenDialog("add")}
        >
          {data?.buttonText}
        </Button>,
      );
    }

    // Add/Cancel Buttons
    if (data?.addButton && data.addPermission) {
      actionButtons.push(
        <Button
          key="cancel-add"
          className={classes.secondaryButton}
          startIcon={<CancelIcon className={classes.buttonIcon} />}
          onClick={data.addCancelClick}
        >
          Cancel
        </Button>,
      );
      actionButtons.push(
        <Button
          key="confirm-add"
          className={classes.successButton}
          startIcon={<AddIcon className={classes.buttonIcon} />}
          onClick={data.addClick}
        >
          Add
        </Button>,
      );
    }

    // Edit Button
    if (data?.editButton) {
      actionButtons.push(
        <Button
          key="edit"
          className={classes.warningButton}
          startIcon={<EditIcon className={classes.buttonIcon} />}
          onClick={data.editButtonClick}
        >
          Edit
        </Button>,
      );
    }

    // Update Buttons
    if (data?.updatePermission && data?.updateButton) {
      actionButtons.push(
        <Button
          key="cancel-update"
          className={classes.secondaryButton}
          startIcon={<CancelIcon className={classes.buttonIcon} />}
          onClick={data?.goBackEditClick}
        >
          Cancel
        </Button>,
      );
      if (data?.deleteButton && data?.deletePermission) {
        actionButtons.push(
          <Button
            key="delete"
            className={classes.deleteButton}
            startIcon={<DeleteIcon className={classes.buttonIcon} />}
            onClick={data.deleteClick}
          >
            Delete
          </Button>,
        );
      }
      actionButtons.push(
        <Button
          key="update"
          className={classes.successButton}
          startIcon={<UpdateIcon className={classes.buttonIcon} />}
          onClick={data.updateClick}
        >
          Update
        </Button>,
      );
    }
    if (data?.showCreateApiButton && data?.handleOpenCreateApiDialog) {
      actionButtons.push(
        <Button
          key="create-api"
          className={classes.warningButton} // or choose another style class
          startIcon={<AddIcon className={classes.buttonIcon} />}
          onClick={data.handleOpenCreateApiDialog}
        >
          {data?.createApiButtonText || "Create API"}
        </Button>,
      );
    }

    // In the renderActionButtons function of TopView component:
    if (data?.toggleOpenToolBar) {
      actionButtons.push(
        <div style={{ position: "relative" }} key="filter-toggle">
          <Button
            className={classes.secondaryButton}
            style={{
              background: data.hasActiveFilters
                ? "linear-gradient(45deg, #FFD700 30%, #FFA000 90%)"
                : "rgba(255, 255, 255, 0.15)",
              color: data.hasActiveFilters ? "#1e243c" : "white",
              fontWeight: data.hasActiveFilters ? 700 : 500,
              border: data.hasActiveFilters
                ? "1px solid #FFA000"
                : "1px solid rgba(255, 255, 255, 0.3)",
            }}
            startIcon={
              data.openToolBar ? (
                <ArrowDropDownIcon className={classes.buttonIcon} />
              ) : (
                <ArrowDropUpIcon className={classes.buttonIcon} />
              )
            }
            onClick={data.toggleOpenToolBar}
          >
            {data.openToolBar ? "Hide Filter" : "Show filter"}
          </Button>

          {/* Optional: Show a badge when filters are active */}
          {data.hasActiveFilters && (
            <Box
              position="absolute"
              top={-6}
              right={-6}
              width={20}
              height={20}
              borderRadius="50%"
              border="2px solid white"
              boxShadow="0 2px 10px rgba(255, 215, 0, 0.6)"
              display="flex"
              alignItems="center"
              justifyContent="center"
              bgcolor="#FFD700"
              borderColor="#FFD700"
            >
              <Typography
                style={{
                  color: "#1e243c",
                  fontSize: "12px",
                  fontWeight: "bold",
                  lineHeight: 1,
                }}
              >
                {data.activeFilterCount}
              </Typography>
            </Box>
          )}
        </div>,
      );
    }
    // Add this after the existing isDialog button logic

    // Add action buttons to the main buttons array
    buttons.push(...actionButtons);

    return buttons;
  };

  return (
    <Paper className={classes.root} elevation={0}>
      <Box className={classes.contentWrapper}>
        <Grid container alignItems="center" className={classes.mobileContainer}>
          {/* Left Section - Title and Breadcrumbs */}
          <Grid>
            <Box className={classes.titleSection}>
              {!data?.hideBtn && (
                <Button
                  className={classes.backButton}
                  onClick={() => routePath("back")}
                  aria-label="Go back"
                >
                  <KeyboardBackspaceIcon />
                </Button>
              )}

              <Box className={classes.titleContainer}>
                <Typography variant="h3" className={classes.mainTitle}>
                  {data?.title || "Page Title"}
                </Typography>

                {(data?.subTitleFirst ||
                  data?.subTitleSecond ||
                  data?.subTitleThird) && (
                  <Box className={classes.breadcrumbsContainer}>
                    <Breadcrumbs
                      separator={<NavigateNextIcon fontSize="small" />}
                      aria-label="breadcrumb"
                    >
                      {createBreadcrumbs()}
                    </Breadcrumbs>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Right Section - Action Buttons */}
          <Grid>
            <Box className={classes.buttonContainer}>
              {renderActionButtons()}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}
